import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const fetchSignatarios = createAsyncThunk("assinaturas/signatarios/fetch", async (search = "", thunkAPI) => {
  try {
    let params = {};

    if (typeof search === "string") {
      if (search) params.search = search;
    } else if (typeof search === "object" && search !== null) {
      params.search = JSON.stringify(search);
    }

    const res = await axios.get("/assinaturas/signatarios", { params });
    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const fetchSignatariosEmpresa = createAsyncThunk("assinaturas/signatarios/empresas/fetch", async ({ empresaId, search = "" }, thunkAPI) => {
  try {
    let params = {};
    if (search) {
      params.search = typeof search === "object" ? JSON.stringify(search) : String(search);
    }

    const res = await axios.get(`/assinaturas/signatarios/empresas/${empresaId}`, { params });
    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const atualizarSignatario = createAsyncThunk("assinaturas/signatarios/atualizar", async ({ signerID, signerData }, thunkAPI) => {
  try {
    const res = await axios.put(`/assinaturas/signatario/${signerID}`, signerData);

    if (res && res.config.data) return JSON.parse(res.config.data);

    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const cadastrarSignatario = createAsyncThunk("assinaturas/signatarios/cadastrar", async (signer, thunkAPI) => {
  try {
    const res = await axios.post("/assinaturas/signatario", signer);
    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const enviarAssinatura = createAsyncThunk("assinaturas/enviar", async ({ anexo, signers }, thunkAPI) => {
  try {
    const res = await axios.post("/assinaturas", {
      anexo,
      signers,
    });
    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const verificarAssinatura = createAsyncThunk("assinaturas/verificar", async (_, thunkAPI) => {
  try {
    const res = await axios.get("/assinaturas/verificar");
    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const dashboard = createAsyncThunk("assinaturas/dashboard", async (params = {}, thunkAPI) => {
  try {
    const res = await axios.get("/assinaturas/dashboard", { params });
    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const reenviarEmails = createAsyncThunk("assinaturas/reenviar/emails", async ({ envelope_id }, thunkAPI) => {
  try {
    const res = await axios.post("/assinaturas/reenviar/emails", {
      envelope_id,
    });
    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const fetchEnvelopesLogs = createAsyncThunk("envelopes/Logs", async ({ id }, thunkAPI) => {
  try {
    return (await axios.get(`/assinaturas/${id}/logs`)).data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const CancelarAssinatura = createAsyncThunk("assinaturas/cancelar", async ({ envelope_id }, thunkAPI) => {
  try {
    const res = await axios.put("/assinaturas/cancelar", {
      envelope_id,
    });
    return res.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// --- Initial State ---
const initialState = {
  items: [], // assinaturas
  signatarios: [], // lista de signatários
  signatariosEmpresas: [], // lista de signatários de empresas
  signatariosPending: [], //lista de signatários que serão enviados e que já existem
  dashboard: null, // dados de dashboard
  loading: false,
  error: null,
};

// --- Slice ---
const assinaturasSlice = createSlice({
  name: "assinaturas",
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
    addPendingSigner: (state, action) => {
      if (!state.signatariosPending.find((s) => s.id === action.payload.id)) {
        state.signatariosPending.push(action.payload);
      }
    },
    removePendingSigner: (state, action) => {
      state.signatariosPending = state.signatariosPending.filter((s) => s.id != action.payload);
    },
  },
  extraReducers: (builder) => {
    // fetchSignatarios
    builder
      .addCase(fetchSignatarios.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSignatarios.fulfilled, (state, action) => {
        state.loading = false;
        state.signatarios = action.payload.map((item) => ({ ...item }));
      })
      .addCase(fetchSignatarios.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // fetchSignatariosEmpresas
    builder
      .addCase(fetchSignatariosEmpresa.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSignatariosEmpresa.fulfilled, (state, action) => {
        state.loading = false;
        state.signatariosEmpresas = action.payload;
      })
      .addCase(fetchSignatariosEmpresa.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // atualizarSignatario
    builder
      .addCase(atualizarSignatario.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(atualizarSignatario.fulfilled, (state, action) => {
        state.loading = false;
        state.signatarios = state.signatarios.map((s) => (s.id === action.payload.id ? action.payload : s));
      })
      .addCase(atualizarSignatario.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(cadastrarSignatario.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cadastrarSignatario.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(cadastrarSignatario.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // enviarAssinatura
    builder
      .addCase(enviarAssinatura.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enviarAssinatura.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(enviarAssinatura.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // dashboard
    builder
      .addCase(dashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(dashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
      })
      .addCase(dashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetError, addPendingSigner, setPendingSigners, removePendingSigner } = assinaturasSlice.actions;
export default assinaturasSlice.reducer;
